import { MarkerType, type Edge } from "@xyflow/react";
import type {
  Connection,
  ConnectionLifetime,
  ConnectionMassState,
  Signature,
  System,
  WhClass,
} from "../types/chain";
import type { XY } from "../store/layoutStore";
import type { SystemFlowNode, SystemNodeData } from "./types";

export type EdgeMeta = {
  title: string;
  [key: string]: unknown;
};

function findLinkedSig(
  c: Connection,
  sysById: Map<string, System>,
): { sig: Signature; side: "from" | "to" } | null {
  if (!c.wh_type) return null;
  const sides: ["from" | "to", System | undefined][] = [
    ["from", sysById.get(c.from)],
    ["to", sysById.get(c.to)],
  ];
  for (const [side, sys] of sides) {
    if (!sys) continue;
    const m = sys.signatures.find(
      (sig) => sig.group === "wormhole" && sig.wh_type === c.wh_type,
    );
    if (m) return { sig: m, side };
  }
  return null;
}

function buildEdgeTitle(
  c: Connection,
  sysById: Map<string, System>,
  destinationFor: (code: string) => WhClass | null,
): string {
  if (!c.wh_type) return "";
  const linked = findLinkedSig(c, sysById);
  const scanId = linked?.sig.scan_id ?? "";
  const cls =
    c.wh_type === "K162" ? "?" : (destinationFor(c.wh_type) ?? "?");
  const parts: string[] = [];
  if (scanId) parts.push(scanId);
  parts.push(cls);
  parts.push(c.wh_type);
  return parts.join(" ");
}

function pickEdgeColor(
  lifetime: ConnectionLifetime,
  mass: ConnectionMassState,
): string {
  if (lifetime === "VEOL" || mass === "critical") return "#e0524a";
  if (mass === "destab") return "#f0a020";
  if (lifetime === "EOL") return "#d946ef";
  return "#9aa3b2";
}

const LIFETIME_DASHARRAY: Record<ConnectionLifetime, string | undefined> = {
  fresh: undefined,
  EOL: "6 4",
  VEOL: "1 4",
};

export function systemsToNodes(
  systems: SystemNodeData[],
  positions: Record<string, XY>,
): SystemFlowNode[] {
  return systems.map((s) => ({
    id: s.id,
    type: "system",
    position: positions[s.id] ?? { x: 0, y: 0 },
    data: s,
  }));
}

export function connectionsToEdges(
  connections: Connection[],
  systems: System[],
  destinationFor: (code: string) => WhClass | null,
): Edge[] {
  const sysById = new Map(systems.map((s) => [s.id, s]));
  return connections.map((c) => {
    const stroke = pickEdgeColor(c.lifetime, c.mass);
    const heavy = c.mass === "critical" || c.lifetime === "VEOL";
    const arrow = { type: MarkerType.ArrowClosed, color: stroke };
    const meta: EdgeMeta = { title: buildEdgeTitle(c, sysById, destinationFor) };
    return {
      id: c.id,
      type: "floating",
      source: c.from,
      target: c.to,
      data: meta,
      style: {
        stroke,
        strokeWidth: heavy ? 3 : 2,
        strokeDasharray: LIFETIME_DASHARRAY[c.lifetime],
      },
      ...(c.direction === "out" ? { markerEnd: arrow } : {}),
      ...(c.direction === "in" ? { markerStart: arrow } : {}),
    };
  });
}
